import {
  Button,
  Card,
  CardContent,
  CardProps,
  Chip,
  Divider,
  Stack,
  Switch,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditIcon from '@mui/icons-material/Edit';
import { User } from 'api-client';
import React from 'react';
import { useAsync } from 'react-components';
import { AppControllerContext } from '../app-contexts';

const StyledCard = styled((props: CardProps) => <Card {...props} />)(() => ({
  overflow: 'visible',
}));

export interface UserProfileCardProps {
  user: User;
  makeAdmin?: (admin: boolean) => Promise<void> | void;
  updateUser?: (fields: {
    email?: string;
    phoneNumber?: string;
    designation?: string;
    password?: string;
  }) => Promise<void> | void;
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <>
      <Stack sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary" letterSpacing={0.8} textTransform="uppercase">
          {label}
        </Typography>
        <Typography variant="body1" color={value ? 'text.primary' : 'text.disabled'} mt={0.5}>
          {value || '—'}
        </Typography>
      </Stack>
      <Divider />
    </>
  );
}

export function UserProfileCard({ user, makeAdmin, updateUser }: UserProfileCardProps): JSX.Element {
  const safeAsync = useAsync();
  const { showAlert } = React.useContext(AppControllerContext);

  const [editing, setEditing] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(user.is_admin);
  const [email, setEmail] = React.useState(user.email ?? '');
  const [phoneNumber, setPhoneNumber] = React.useState(user.phone_number ?? '');
  const [designation, setDesignation] = React.useState(user.designation ?? '');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');

  // Sync state when user prop changes (after refresh)
  React.useEffect(() => {
    setIsAdmin(user.is_admin);
    setEmail(user.email ?? '');
    setPhoneNumber(user.phone_number ?? '');
    setDesignation(user.designation ?? '');
  }, [user]);

  const handleCancel = () => {
    setEditing(false);
    setIsAdmin(user.is_admin);
    setEmail(user.email ?? '');
    setPhoneNumber(user.phone_number ?? '');
    setDesignation(user.designation ?? '');
    setPassword('');
    setConfirmPassword('');
    setPasswordError('');
  };

  const handleSave = async () => {
    if (password && password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    setPasswordError('');
    setSaving(true);
    try {
      if (isAdmin !== user.is_admin && makeAdmin) {
        await safeAsync(makeAdmin(isAdmin));
      }
      if (updateUser) {
        await safeAsync(
          updateUser({
            email: email || undefined,
            phoneNumber: phoneNumber || undefined,
            designation: designation || undefined,
            password: password || undefined,
          }),
        );
      }
      setPassword('');
      setConfirmPassword('');
      setEditing(false);
    } catch (e) {
      showAlert('error', `Failed to update user: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <StyledCard variant="outlined">
      {/* ── Header ── */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <AccountCircleIcon sx={{ fontSize: 56, color: 'grey.400' }} />
        <Stack flex={1}>
          <Typography variant="h5" fontWeight={700}>{user.username}</Typography>
          <Chip
            label={user.is_admin ? 'Admin' : 'User'}
            color={user.is_admin ? 'primary' : 'default'}
            size="small"
            sx={{ width: 'fit-content', mt: 0.5 }}
          />
        </Stack>
        {!editing && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => setEditing(true)}
          >
            Edit
          </Button>
        )}
        {editing && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Editing...
          </Typography>
        )}
      </Stack>

      {/* ── Read Mode ── */}
      {!editing && (
        <CardContent sx={{ p: 0 }}>
          <InfoRow label="Username" value={user.username} />
          <InfoRow label="Email" value={user.email} />
          <InfoRow label="Phone Number" value={user.phone_number} />
          <InfoRow label="Designation" value={user.designation} />
          <Stack sx={{ px: 3, py: 1.5 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" letterSpacing={0.8} textTransform="uppercase">
              Role
            </Typography>
            <Stack mt={0.5}>
              <Chip
                label={user.is_admin ? 'Admin' : 'User'}
                color={user.is_admin ? 'primary' : 'default'}
                size="small"
                sx={{ width: 'fit-content' }}
              />
            </Stack>
          </Stack>
         {/*  <Divider /> */}
        </CardContent>
      )}

      {/* ── Edit Mode ── */}
      {editing && (
        <CardContent>
          <Typography variant="overline" fontWeight={700} color="text.secondary" display="block" mb={2}>
            Profile Information
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Email (optional)"
              type="email"
              fullWidth
              variant="outlined"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
            />
            <TextField
              label="Phone Number (optional — numbers only)"
              fullWidth
              variant="outlined"
              value={phoneNumber}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              onChange={(ev) => setPhoneNumber(ev.target.value.replace(/\D/g, ''))}
            />
            <TextField
              label="Designation (optional)"
              fullWidth
              variant="outlined"
              value={designation}
              onChange={(ev) => setDesignation(ev.target.value)}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'grey.50' }}
            >
              <Stack>
                <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
                  Role
                </Typography>
                <Typography variant="body2" mt={0.5}>Admin privileges</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" color={isAdmin ? 'primary' : 'text.secondary'} fontWeight={600}>
                  {isAdmin ? 'ON' : 'OFF'}
                </Typography>
                <Switch
                  checked={isAdmin}
                  onChange={(ev) => setIsAdmin(ev.target.checked)}
                  color="primary"
                />
              </Stack>
            </Stack>
          </Stack>

          {/*  <Divider sx={{ my: 3, borderStyle: 'dashed' }} /> */}

          <Typography variant="overline" fontWeight={700} color="text.secondary" display="block" mb={2}>
            Change Password{' '}
            <Typography component="span" variant="caption" color="text.disabled" fontWeight={400}>
              (leave blank to keep current)
            </Typography>
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="New Password"
              type="password"
              fullWidth
              variant="outlined"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              error={!!passwordError}
            />
            <TextField
              label="Confirm Password"
              type="password"
              fullWidth
              variant="outlined"
              value={confirmPassword}
              onChange={(ev) => setConfirmPassword(ev.target.value)}
              error={!!passwordError}
              helperText={passwordError}
            />
          </Stack>

           <Divider sx={{ mt: 3, mb: 2 }} />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        </CardContent>
      )}
    </StyledCard>
  );
}
